import React, { useState, useEffect, useContext } from "react"; // Import useContext
import "./Videos.css"; // Import the custom CSS
import { LanguageContext } from "../context/LanguageContext"; // Import LanguageContext

// Translatable content definitions
const enContent = {
  videoLibraryTitle: "Video Library",
  videoLibrarySubtitle:
    "Explore a wide range of educational videos tailored for special education.",
  instructorLabel: "Instructor:",
  noVideosAvailable: "No videos available in this category.",
};

const urContent = {
  videoLibraryTitle: "ویڈیو لائبریری", // Video Library
  videoLibrarySubtitle:
    "خصوصی تعلیم کے لیے تیار کردہ تعلیمی ویڈیوز کی ایک وسیع رینج دریافت کریں۔", // Explore a wide range of educational videos tailored for special education.
  instructorLabel: "انسٹرکٹر:", // Instructor:
  noVideosAvailable: "اس زمرے میں کوئی ویڈیو دستیاب نہیں ہے۔", // No videos available in this category.
};

const VideoItem = ({ video, onVideoSelect, isSelected }) => {
  const { title, videoId, instructor } = video;
  const [videos, setVideos] = useState([]); // This state is unused in VideoItem

  // YouTube thumbnail URL - using medium resolution for smaller thumbnails
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

  const handleVideoClick = () => {
    onVideoSelect(video);
  };

  return (
    <div
      className={`video-item ${isSelected ? "selected" : ""}`}
      onClick={handleVideoClick}
    >
      <div className="video-thumbnail">
        <img
          src={thumbnailUrl}
          alt={title}
          onError={(e) => {
            e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          }}
        />
        {/* Play button overlay */}
        <div className="play-overlay">
          <div className="play-button">
            <svg className="play-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>

      <div className="video-info">
        <h3 className="video-title" data-tts="true">
          {title}
        </h3>
        {instructor && (
          <p data-tts="true" className="video-instructor">
            {instructor}
          </p>
        )}
      </div>
    </div>
  );
};

const VideoPlayer = ({ video, onClose }) => {
  const { language } = useContext(LanguageContext); // Consume LanguageContext
  const content = language === "en" ? enContent : urContent; // Get translated content

  if (!video) return null;

  const embedUrl = `https://www.youtube.com/embed/${video.videoId}?autoplay=1`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 data-tts="true" lassName="modal-title">
            {video.title}
          </h2>
          <button onClick={onClose} className="close-button">
            <img src="/assets/black-x.png"></img>
          </button>
        </div>
        <div className="video-player">
          <iframe
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <h4 data-tts="true" className="modal-desc">
          {video.subtitle}
        </h4>
        {video.instructor && (
          <div className="modal-footer">
            <p data-tts="true" className="instructor-info">
              <span className="instructor-label">
                {content.instructorLabel}
              </span>{" "}
              {/* Translated label */}
              {video.instructor}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab] = useState("all"); // activeTab is unused, consider removing if not needed

  const { language } = useContext(LanguageContext); // Consume LanguageContext
  const content = language === "en" ? enContent : urContent; // Get translated content

  // Mock data with more realistic content
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/videos`);
        const data = await response.json();
        setVideos(data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchPosts();
  }, []);

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handleClosePlayer = () => {
    setSelectedVideo(null);
  };

  const filteredVideos = videos;

  return (
    <div className="video-library">
      {/* Header */}

      <div className="main-content">
        {/* Page Title */}
        <div className="page-title">
          <h1 data-tts="true">{content.videoLibraryTitle}</h1>{" "}
          {/* Translated title */}
          <p data-tts="true">
            {content.videoLibrarySubtitle} {/* Translated subtitle */}
          </p>
        </div>

        {/* Content Sections */}
        <div className="video-grid">
          {filteredVideos.map((video) => (
            <VideoItem
              key={video.id}
              video={video}
              onVideoSelect={handleVideoSelect}
              isSelected={selectedVideo?.id === video.id}
            />
          ))}
        </div>

        {filteredVideos.length === 0 && (
          <div className="empty-state">
            <p data-tts="true" className="empty-text">
              {content.noVideosAvailable}
            </p>{" "}
            {/* Translated empty state message */}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      <VideoPlayer video={selectedVideo} onClose={handleClosePlayer} />
    </div>
  );
};

export default Videos;
